import { createContext, type ComponentChildren } from "preact";
import { useContext, useEffect, useMemo, useState } from "preact/hooks";
import { defaultBrandSettings } from "../services/defaultStates";
import { isSupportedFont, loadGoogleFont } from "../utils/googleFonts";
import { resolveThemeMode, type ThemeMode } from "./resolveThemeMode";
import { resolveThemeTokens } from "./resolveThemeTokens";

export type { ThemeMode };

/**
 * The theme mode is normally fixed for the session by the `theme` query param
 * (`light` | `dark`). The Checkout Studio preview is the exception — it flips
 * the mode live via `setResolvedMode` as the merchant toggles it.
 */
function getThemeParam(): string | null {
  return new URLSearchParams(window.location.search).get("theme");
}

type ThemeContextType = {
  brandSettings: BrandSettings;
  setBrandSettings: (settings: BrandSettings) => void;
  resolvedMode: ThemeMode;
  setResolvedMode: (mode: ThemeMode) => void;
};

const fallback: ThemeContextType = {
  brandSettings: defaultBrandSettings,
  setBrandSettings: () => undefined,
  resolvedMode: "light",
  setResolvedMode: () => undefined,
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export default function ThemeProvider({
  children,
}: {
  children: ComponentChildren;
}) {
  const [brandSettings, setBrandSettings] =
    useState<BrandSettings>(defaultBrandSettings);

  const [resolvedMode, setResolvedMode] = useState<ThemeMode>(() =>
    resolveThemeMode(getThemeParam())
  );

  useEffect(() => {
    const root = document.documentElement;

    root.dataset.theme = resolvedMode;
    root.classList.toggle("dark", resolvedMode === "dark");

    if (isSupportedFont(brandSettings.font_family)) {
      loadGoogleFont(brandSettings.font_family);
    }

    const vars = resolveThemeTokens(brandSettings, resolvedMode);
    Object.entries(vars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
      else root.style.removeProperty(name);
    });
  }, [resolvedMode, brandSettings]);

  const value = useMemo<ThemeContextType>(
    () => ({
      brandSettings,
      setBrandSettings,
      resolvedMode,
      setResolvedMode,
    }),
    [brandSettings, resolvedMode]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    console.warn("useTheme must be used within <ThemeProvider />");
    return fallback;
  }
  return context;
};
